import { feedbackUuid } from './feedbackRetention.ts';
import type { Database } from './types/feedbackDatabase.generated';
import type { FeedbackItem } from './feedback';

export type RawFeedbackCompletion =
  Database['public']['Functions']['list_my_feedback_completions']['Returns'][number];
type RequiredCompletionKeys =
  | 'completion_id'
  | 'feedback_id'
  | 'ticket'
  | 'completed_at'
  | 'active';
export type FeedbackCompletion = Omit<
  RawFeedbackCompletion,
  RequiredCompletionKeys
> & {
  [K in RequiredCompletionKeys]: NonNullable<RawFeedbackCompletion[K]>;
};
export type ConfirmFeedbackArgs =
  Database['public']['Functions']['confirm_feedback']['Args'];
export type ConfirmFeedbackReceipt =
  Database['public']['Functions']['confirm_feedback']['Returns'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
function string(value: unknown, name: string): string {
  if (typeof value !== 'string' || !value.length)
    throw new Error(`Invalid feedback ${name}`);
  return value;
}
function uuid(value: unknown, name: string): string {
  if (!feedbackUuid(value)) throw new Error(`Invalid feedback ${name}`);
  return value;
}
function boolean(value: unknown, name: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`Invalid feedback ${name}`);
  return value;
}
function date(value: unknown, name: string): string {
  const text = string(value, name);
  if (!Number.isFinite(Date.parse(text)))
    throw new Error(`Invalid feedback ${name}`);
  return text;
}
function nullable<T>(
  value: unknown,
  name: string,
  parse: (value: unknown, name: string) => T,
): T | null {
  return value === null ? null : parse(value, name);
}
function platform(value: unknown, name: string): string {
  if (value !== 'web' && value !== 'android' && value !== 'ios')
    throw new Error(`Invalid feedback ${name}`);
  return value;
}
function version(value: unknown, name: string): string {
  const text = string(value, name);
  if (!/^[0-9A-Za-z.+_-]{1,64}$/.test(text))
    throw new Error(`Invalid feedback ${name}`);
  return text;
}
function note(value: unknown, name: string): string {
  const text = string(value, name);
  if ([...text].length > 2000)
    throw new Error('Keep test notes under 2,000 characters');
  return text;
}
function delivery(value: unknown, name: string): string {
  if (
    value !== 'pending' &&
    value !== 'running' &&
    value !== 'retry' &&
    value !== 'sent'
  )
    throw new Error(`Invalid feedback ${name}`);
  return value;
}

/** SQL RETURNS TABLE admits nulls in its contract; validate before narrowing required fields. */
export function parseFeedbackCompletion(value: unknown): FeedbackCompletion {
  if (!isRecord(value)) throw new Error('Invalid feedback completion');
  const ticket = string(value.ticket, 'ticket');
  if (!/^[A-Z]+-[0-9]+$/.test(ticket))
    throw new Error('Invalid feedback ticket');
  return {
    completion_id: uuid(value.completion_id, 'completion ID'),
    feedback_id: uuid(value.feedback_id, 'report ID'),
    ticket,
    completed_at: date(value.completed_at, 'completion date'),
    active: boolean(value.active, 'active state'),
    confirmation_id: nullable(value.confirmation_id, 'confirmation ID', uuid),
    works: nullable(value.works, 'answer', boolean),
    note: nullable(value.note, 'note', note),
    tested_platform: nullable(value.tested_platform, 'platform', platform),
    tested_app_version: nullable(
      value.tested_app_version,
      'app version',
      version,
    ),
    confirmed_at: nullable(value.confirmed_at, 'confirmation date', date),
    ticket_delivery_state: nullable(
      value.ticket_delivery_state,
      'ticket delivery',
      delivery,
    ),
    ticket_delivery_error: nullable(
      value.ticket_delivery_error,
      'ticket error',
      string,
    ),
  };
}
export function parseFeedbackCompletions(value: unknown): FeedbackCompletion[] {
  if (!Array.isArray(value))
    throw new Error('Invalid feedback completion history');
  return value.map(parseFeedbackCompletion);
}

/** Validate and freeze exactly the arguments the server accepts; owner and ticket are server-derived. */
export function parseConfirmFeedbackArgs(
  value: unknown,
): Readonly<ConfirmFeedbackArgs> {
  if (!isRecord(value)) throw new Error('Invalid feedback confirmation');
  const allowed = [
    'p_id',
    'p_completion',
    'p_works',
    'p_note',
    'p_platform',
    'p_app_version',
  ];
  if (Object.keys(value).some((key) => !allowed.includes(key)))
    throw new Error('Invalid feedback confirmation fields');
  return Object.freeze({
    p_id: uuid(value.p_id, 'request ID'),
    p_completion: uuid(value.p_completion, 'completion ID'),
    p_works: boolean(value.p_works, 'answer'),
    p_note: nullable(value.p_note, 'note', note),
    p_platform: platform(value.p_platform, 'platform'),
    p_app_version: version(value.p_app_version, 'app version'),
  });
}

/** Saved receipt and provider delivery are separate facts, including for superseded history. */
export function feedbackConfirmationLabel(
  completion: FeedbackCompletion,
): string {
  const history = completion.active ? '' : 'Superseded · ';
  if (!completion.confirmation_id)
    return completion.active
      ? 'Ready to test'
      : 'Superseded · Test request closed';
  const answer =
    completion.works === false
      ? 'Still broken'
      : completion.works === true
        ? 'Works'
        : 'Answer unavailable';
  const delivery =
    completion.ticket_delivery_state === 'sent'
      ? `Added to ${completion.ticket}`
      : `ticket update pending${completion.ticket_delivery_error ? ` · ${completion.ticket_delivery_error}` : ''}`;
  return `${history}Saved · ${answer} · ${delivery}`;
}

/** Join only the already loaded owned reports; a completion ID never authorizes another lookup. */
export function reportForCompletion(
  completion: FeedbackCompletion,
  reports: readonly FeedbackItem[],
): FeedbackItem | undefined {
  return reports.find((report) => report.id === completion.feedback_id);
}

/** Receipt validation is shared by the RPC boundary and the submission lifecycle. */
export function requireFeedbackConfirmationReceipt(
  request: Readonly<ConfirmFeedbackArgs>,
  receipt: unknown,
): string {
  if (!feedbackUuid(receipt) || receipt !== request.p_id)
    throw new Error(
      'The server receipt did not match; retry the same confirmation',
    );
  return receipt;
}

type ConfirmationSnapshot = Readonly<{
  works: boolean | null;
  note: string;
  sending: boolean;
  saved: boolean;
  error: { cause: unknown } | null;
  refreshError: { cause: unknown } | null;
  request: Readonly<ConfirmFeedbackArgs> | null;
}>;

/** One immutable submission lifecycle, observed by either platform without a React dependency. */
export function createFeedbackConfirmation(options: {
  ownerId: string;
  completionId: string;
  platform: string;
  appVersion: string;
  currentOwner: () => string | null;
  newId: () => string;
  confirm: (
    owner: string,
    request: Readonly<ConfirmFeedbackArgs>,
  ) => Promise<unknown>;
  invalidate: (owner: string, includeReport: boolean) => Promise<unknown>;
}) {
  let snapshot: ConfirmationSnapshot = {
    works: null,
    note: '',
    sending: false,
    saved: false,
    error: null,
    refreshError: null,
    request: null,
  };
  const listeners = new Set<() => void>();
  let busy = false;
  const visible = () =>
    listeners.size > 0 && options.currentOwner() === options.ownerId;
  const update = (changes: Partial<ConfirmationSnapshot>) => {
    snapshot = { ...snapshot, ...changes };
    for (const listener of listeners) listener();
  };
  const editable = () =>
    visible() && !snapshot.request && !busy && !snapshot.saved;
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    setWorks(works: boolean) {
      if (editable()) update({ works });
    },
    setNote(note: string) {
      if (editable()) update({ note });
    },
    async send(
      completion: Pick<FeedbackCompletion, 'active' | 'confirmation_id'>,
    ) {
      if (
        !visible() ||
        busy ||
        snapshot.saved ||
        !completion.active ||
        completion.confirmation_id ||
        snapshot.works === null
      )
        return;
      busy = true;
      update({ sending: true, error: null, refreshError: null });
      try {
        const request =
          snapshot.request ??
          parseConfirmFeedbackArgs({
            p_id: options.newId(),
            p_completion: options.completionId,
            p_works: snapshot.works,
            p_note: snapshot.note === '' ? null : snapshot.note,
            p_platform: options.platform,
            p_app_version: options.appVersion,
          });
        if (!snapshot.request) update({ request });
        requireFeedbackConfirmationReceipt(
          request,
          await options.confirm(options.ownerId, request),
        );
        if (visible()) update({ saved: true, note: '', works: null });
        // A confirmed A receipt must refresh A even if the mounted editor now belongs to B.
        await options.invalidate(options.ownerId, false);
      } catch (cause) {
        if (visible()) update({ error: { cause } });
        // These SQL conflicts mean cached cycle authority has changed. Network and
        // receipt failures remain exact-request retries without implying a conflict.
        if (
          typeof cause === 'object' &&
          cause !== null &&
          'code' in cause &&
          cause.code === '23505' &&
          'message' in cause &&
          (cause.message === 'Feedback completion is inactive' ||
            cause.message === 'Feedback completion already answered')
        ) {
          try {
            await options.invalidate(options.ownerId, true);
          } catch (refreshCause) {
            if (visible()) update({ refreshError: { cause: refreshCause } });
          }
        }
      } finally {
        busy = false;
        if (visible()) update({ sending: false });
      }
    },
  };
}
